import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDepartures, getDeparturesSince, type Departure } from '@/services/departures';
import { useFavouriteStops } from '@/store/favourites';
import { searchStops } from '@/services/stop-search';
import { canPoll } from '@/services/polling';
import './StopDetailScreen.css';

const MODE_COLORS: Record<string, string> = {
  Train: '#F6891F', Bus: '#0072C6', Ferry: '#61B255', Metro: '#0099CD', 'Light Rail': '#D81E34',
};
const BADGE_LETTER: Record<string, string> = {
  Train: 'T', Bus: 'B', Ferry: 'F', Metro: 'M', 'Light Rail': 'L',
};

function formatCountdown(scheduled: string, predicted?: string, delayMin?: number): string {
  if (!scheduled) return '—';
  const target = predicted ? new Date(predicted) : new Date(scheduled);
  const now = new Date();
  const diff = Math.round((target.getTime() - now.getTime()) / 60000);
  if (diff <= -1) return `${Math.abs(diff)}m ago`;
  if (diff <= 0) return 'Now';
  if (diff < 60) return `${diff} min`;
  if (diff < 24 * 60) {
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  return new Date(scheduled).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function timeStr(iso: string) { return iso ? new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '—'; }

export function StopDetailScreen() {
  const { stopId } = useParams<{ stopId: string }>();
  const navigate = useNavigate();
  const { stops, add, remove } = useFavouriteStops();
  const [departures, setDepartures] = useState<Departure[]>([]);
  const [loading, setLoading] = useState(true);
  const [stopName, setStopName] = useState('');
  const [error, setError] = useState('');
  const [modeFilter, setModeFilter] = useState('All');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const pastDeparturesRef = useRef<{ date: string; data: Departure[] } | null>(null);

  const isFav = stopId ? stops.some((s) => s.id === stopId) : false;

  const load = useCallback(async (initial = false) => {
    const id = stopId;
    if (!id || (!initial && (!canPoll()))) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    if (initial) setLoading(true);
    setError('');
    try {
      const today = new Date();
      const yyyymmdd = today.toISOString().slice(0, 10).replace(/-/g, '');
      const cachedPast = pastDeparturesRef.current?.date === yyyymmdd ? pastDeparturesRef.current.data : undefined;
      // Current Sydney time as HHMM for the paging stop condition.
      const nowSydney = new Date().toLocaleString('en-CA', {
        timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).replace(':', '');
      const [nowDeps, pastDeps] = await Promise.all([
        // Download the complete 24-hour timetable once. Polls only request a
        // compact live window and merge realtime changes into that baseline.
        getDepartures(id, initial ? 500 : 50, controller.signal),
        cachedPast
          ? Promise.resolve(cachedPast)
          : getDeparturesSince(id, yyyymmdd, '0000', nowSydney, controller.signal)
              .then(data => {
                pastDeparturesRef.current = { date: yyyymmdd, data };
                return data;
              })
              .catch((e) => {
                if (e instanceof DOMException && e.name === 'AbortError') throw e;
                console.warn('Failed to fetch past departures:', e);
                return [] as Departure[];
              }),
      ]);
      const mergeDepartures = (...groups: Departure[][]) => {
        const merged = new Map<string, Departure>();
        for (const group of groups) for (const d of group) merged.set(`${d.tripId}|${d.scheduledTime}`, d);
        return [...merged.values()].sort((a, b) => new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime());
      };
      if (initial) setDepartures(mergeDepartures(pastDeps, nowDeps));
      else setDepartures(current => mergeDepartures(current, nowDeps));
      if (!stopNameRef.current) {
        try { const results = await searchStops(id, controller.signal); if (results.length > 0) { setStopName(results[0].name); stopNameRef.current = true; } } catch {}
      }
      setLastRefresh(new Date());
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        if (initial) setLoading(false);
      }
    }
  }, [stopId]);

  const stopNameRef = useRef(false);

  useEffect(() => {
    void load(true);
    intervalRef.current = setInterval(() => { void load(); }, 30000);
    const resume = () => {
      if (canPoll()) void load();
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      requestRef.current?.abort();
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
    };
  }, [load]);

  const nextServiceRef = useRef<HTMLDivElement>(null);
  const initialScrollDone = useRef(false);

  // Auto-scroll so the next service is visible on initial load (only once)
  useEffect(() => {
    if (!loading && !initialScrollDone.current && nextServiceRef.current) {
      initialScrollDone.current = true;
      nextServiceRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading]);

  // Split into past and future
  const now = new Date();
  const past = departures.filter(d => new Date(d.scheduledTime) <= now).sort((a, b) => new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime());
  const nextDay = now.getTime() + 24 * 60 * 60 * 1000;
  const future = departures.filter(d => {
    const time = new Date(d.scheduledTime).getTime();
    return time > now.getTime() && time <= nextDay;
  });
  const filteredFuture = modeFilter === 'All' ? future : future.filter(d => d.mode === modeFilter);
  const filteredPast = modeFilter === 'All' ? past : past.filter(d => d.mode === modeFilter);
  const allModes = [...new Set(departures.map(d => d.mode))].sort();

  const handleMapClick = () => {
    navigate(`/map?stop=${stopId}`);
  };

  return (
    <main className="page-container">
      <button className="back-button" onClick={() => navigate(-1)}>← Back</button>

      <div className="stop-header">
        <div>
          <h1 className="stop-title">{stopName || stopId || 'Stop Detail'}</h1>
          <p className="stop-id">Stop ID: {stopId}</p>
        </div>
        <div className="stop-header-right">
          <button className="refresh-btn-small" onClick={() => load()} disabled={loading} title="Force refresh">🔄</button>
          <button className="stop-map-btn" onClick={handleMapClick} title="Show on map">🗺 Map</button>
          <button className={`fav-btn${isFav ? ' fav-btn--active' : ''}`}
            onClick={() => { if (isFav) remove(stopId!); else add({ id: stopId!, name: stopName || stopId! }); }}>
            {isFav ? '★' : '☆'}
          </button>
        </div>
      </div>

      {/* Mode filter pills */}
      {allModes.length > 1 && (
        <div className="route-filters">
          <button className={`route-pill${modeFilter === 'All' ? ' route-pill--active' : ''}`} onClick={() => setModeFilter('All')}>All</button>
          {allModes.map(m => (
            <button key={m} className={`route-pill${modeFilter === m ? ' route-pill--active' : ''}`}
              onClick={() => setModeFilter(modeFilter === m ? 'All' : m)}>
              <span className="route-pill-badge" style={{ background: MODE_COLORS[m] || '#666' }}>{BADGE_LETTER[m] || m[0]}</span> {m}
            </button>
          ))}
        </div>
      )}

      <div className="stop-meta">
        <span className="stop-refresh-time">Updated: {lastRefresh.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
        {loading && <span className="stop-loading-inline"> Refreshing...</span>}
      </div>

      {error && <div className="planner-error">{error}</div>}

      {!loading && filteredPast.length === 0 && filteredFuture.length === 0 && !error && (
        <div className="placeholder-screen"><span className="icon">🕐</span><p className="label">No departures found</p></div>
      )}

      {!loading && (
        <div className="stop-timetable">
          {filteredPast.length > 0 && (
            <section>
              <h2 className="timetable-section-title">Past services (today)</h2>
              <div className="card past-card">
                {filteredPast.map((dep) => <DepartureRow key={'p' + dep.tripId + '-' + dep.scheduledTime} dep={dep} isPast stopId={stopId} />)}
              </div>
            </section>
          )}
          {/* Scroll anchor: sits at the boundary between past and future */}
          <div ref={nextServiceRef} />
          {filteredFuture.length > 0 && (
            <section>
              <h2 className="timetable-section-title">Departures</h2>
              <div className="card">{filteredFuture.map((dep) => <DepartureRow key={dep.tripId + '-' + dep.scheduledTime} dep={dep} stopId={stopId} />)}</div>
            </section>
          )}
        </div>
      )}
    </main>
  );
}

// Occupancy icons
function occupancyIcon(routeName: string, platform?: string): string {
  const hasCarriages = routeName?.toLowerCase().includes('line') || routeName?.toLowerCase().includes('train');
  if (hasCarriages) return '🚃🚃🚃';
  if (platform) return '🚌';
  return '';
}

function DepartureRow({ dep, isPast, stopId }: { dep: Departure; isPast?: boolean; stopId?: string }) {
  const navigate = useNavigate();
  const color = MODE_COLORS[dep.mode] || '#9E9E9E';
  return (
    <button className={`departure-row${isPast ? ' departure-row--past' : ''}`}
      style={{ cursor: 'pointer', width: '100%', textAlign: 'left' }}
      onClick={() => navigate(`/route-map?trip=${encodeURIComponent(dep.tripId)}&stop=${stopId || ''}&mode=${dep.mode}&route=${encodeURIComponent(dep.routeShortName || dep.routeNumber)}&time=${encodeURIComponent(dep.scheduledTime)}&dest=${encodeURIComponent(dep.destination)}`)}>
      <div className="departure-mode-badge" style={{ background: color }}>{BADGE_LETTER[dep.mode] || dep.mode[0]}</div>
      <div className="departure-info">
        <span className="departure-route">{dep.routeNumber}</span>
        <span className="departure-dest">to {dep.destination}</span>
        {dep.platform && <span className="departure-platform">Plat {dep.platform}</span>}
        <span className="departure-occupancy">{occupancyIcon(dep.routeName, dep.platform)}</span>
      </div>
      <div className="departure-time">
        <span className="departure-countdown">{formatCountdown(dep.scheduledTime, dep.predictedTime, dep.delayMinutes)}</span>
        <span className="departure-sched">{timeStr(dep.predictedTime || dep.scheduledTime)}</span>
        {dep.isRealtime && dep.delayMinutes != null && dep.delayMinutes !== 0 && (
          <span className={`departure-live departure-live--${dep.delayMinutes > 0 ? 'delay' : 'ontime'}`}>
            {dep.delayMinutes > 0 ? `+${dep.delayMinutes}m` : 'Early'}
          </span>
        )}
      </div>
    </button>
  );
}

